import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Divider,
  styled,
  keyframes,
  Paper,
} from "@mui/material";
import {
  Lock,
  Email,
  Visibility,
  VisibilityOff,
  GitHub,
  Google,
  Microsoft,
} from "@mui/icons-material";
import { login } from "../services/api";
import { toast, ToastContainer } from "react-toastify";

// Animation
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const AuthContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "100vh",
  background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(3),
}));

const AuthCard = styled(Paper)(({ theme }) => ({
  display: "flex",
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
  maxWidth: "900px",
  width: "100%",
  animation: `${fadeIn} 0.6s ease-out`,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const BrandSection = styled(Box)(({ theme }) => ({
  flex: 1,
  background: "linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)",
  color: "white",
  padding: theme.spacing(6),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(4),
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(6),
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(4),
  },
}));

const SocialButton = styled(Button)(({ theme }) => ({
  flex: 1,
  padding: "10px",
  borderRadius: "8px",
  textTransform: "none",
  fontWeight: 500,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
  },
}));

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials] = useState({
    Username: "",
    Password: "",
  });
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    const loginApi = await login(credentials);
    if (loginApi && loginApi.success) {
      localStorage.setItem("user", JSON.stringify(loginApi.data));
      localStorage.setItem("isAuthenticated", "true");
      navigate("/dashboard");
    } else {
      console.log("loginAPI;:", loginApi);
      return toast.error(loginApi.message);
    }
  };

  return (
    <AuthContainer>
      <ToastContainer />
      <AuthCard elevation={0}>
        {/* Branding Section */}
        <BrandSection>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
              Buildhub <span style={{ fontWeight: 300 }}>Ticketing</span>
            </Typography>
            <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
              Resource Ticketing System
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              maxWidth: "300px",
              mb: 4,
            }}
          ></Box>

          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Secure access to your company resources
          </Typography>
        </BrandSection>

        {/* Login Form Section */}
        <FormSection>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Welcome back
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Sign in to your account
          </Typography>

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Username"
              variant="outlined"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              value={credentials.Username}
              onChange={(e) =>
                setCredentials({ ...credentials, Username: e.target.value })
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              value={credentials.Password}
              onChange={(e) =>
                setCredentials({ ...credentials, Password: e.target.value })
              }
              sx={{ mb: 1 }}
            />

            {/* <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 3,
              }}
            >
              <Button
                size="small"
                color="primary"
                sx={{ textTransform: "none" }}
              >
                Forgot password?
              </Button>
            </Box> */}

            <Button
              fullWidth
              size="large"
              variant="contained"
              type="submit"
              sx={{
                mt: 2,
                mb: 3,
                py: 1.5,
                borderRadius: "8px",
                background: "linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)",
                boxShadow: "0 4px 6px rgba(211, 47, 47, 0.2)",
                "&:hover": {
                  boxShadow: "0 6px 8px rgba(211, 47, 47, 0.3)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Sign In
            </Button>
          </form>

          <Typography variant="body2" color="text.secondary" align="center">
            Don't have an account?{" "}
            <Button size="small" color="primary" sx={{ textTransform: "none" }}>
              Contact IT Department
            </Button>
          </Typography>
        </FormSection>
      </AuthCard>
    </AuthContainer>
  );
}

export default Login;
